import {
    Drawer,
    DrawerContent,
    DrawerItem,
    DrawerItemProps,
    DrawerSelectEvent,
} from '@progress/kendo-react-layout'
import SidebarItem from 'components/Sidebar/SidebarItem'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import React from 'react'
import { MouseEvent, useEffect } from 'react'
import { classNames } from 'utils/dom-helpers'
import { useTenantContext } from '../../../context/TenantContext'
import TenantSelector from './TenantSelector'

export type SidebarItem = {
    translation_key?: string
    text?: string
    separator?: boolean
    icon?: string
    selected?: boolean
    onClickCapture?(): void
    route?: string
    expand_button?: boolean
    external_link?: string
    open_modal?: boolean
    is_beta?: boolean
    is_new?: boolean
    is_hidden?: boolean
    is_coming_soon?: boolean
    [key: string]: any;
    id?: string;
}

interface Props {
    children: React.ReactNode,
    positionMode?: "start" | "end",
    mode?: "overlay" | "push",
    isMini?: boolean,
    isExpanded?: boolean,
}

export const default_sidebar_items: SidebarItem[] = [
    {
        is_hidden: false,
    },
    // {
    //     translation_key: 'home',
    //     text: 'Home',
    //     icon: 'feather-sidebar',
    //     route: '/dashboard',
    //     is_hidden: true
    // },
    {
        translation_key: 'booking_page',
        text: 'Booking Page',
        icon: 'feather-watch',
        external_link: "",
        is_hidden: false,
        id: 'booking_page'
    },
    {
        translation_key: 'calendar',
        text: 'Calendar',
        icon: 'feather-calendar',
        route: '/dashboard/calendar',
        is_hidden: false,
        id: 'calendar'
    },
    {
        translation_key: 'clients',
        text: 'Clients',
        icon: 'feather-users',
        route: '/dashboard/clients',
        is_hidden: false,
        id: 'clients'
    },
    {
        translation_key: 'coupons',
        text: 'Coupons',
        icon: 'feather-file-text',
        route: '/dashboard/coupons',
        is_hidden: false,
        id: 'coupons'
    },
    {
        translation_key: 'sidebar.forms',
        text: 'Forms',
        icon: 'feather-clipboard',
        route: '/dashboard/forms',
        is_beta: true,
        is_hidden: false,
        id: 'forms'
    },
    {
        translation_key: 'locations',
        text: 'Locations',
        icon: 'feather-map-pin',
        route: '/dashboard/locations',
        is_hidden: false,
        id: 'locations'
    },
    // {
    //     translation_key: 'products',
    //     text: 'Products',
    //     icon: 'feather-shopping-bag',
    //     route: '/dashboard/products',
    //     is_hidden: true
    // },
    {
        translation_key: 'memberships',
        text: 'Memberships',
        icon: 'feather-gift',
        route: '/dashboard/memberships',
        is_hidden: false,
        id: 'memberships'
    },

    {
        translation_key: 'notifications',
        text: 'Notifications',
        icon: 'feather-bell',
        route: '/dashboard/notifications',
        is_hidden: false,
        id: 'notifications'
    },
    {
        translation_key: 'point_of_sale',
        text: 'Point of Sale',
        icon: 'feather-dollar-sign',
        route: '/dashboard/sales',
        // open_modal: true,
        is_hidden: false,
        id: 'point_of_sale'
    },
    {
        translation_key: 'reports',
        text: 'Reports',
        icon: 'k-i-star-outline',
        route: '/dashboard/reports',
        is_hidden: false,
        id: 'reports'
    },
    // {
    //     translation_key: 'resources',
    //     text: 'Resources',
    //     icon: 'feather-folder',
    //     route: '/dashboard/resources',
    //     is_hidden: true,
    //     is_coming_soon: true
    // },
    {
        translation_key: 'services',
        text: 'Services',
        icon: 'feather-briefcase',
        route: '/dashboard/services',
        is_hidden: false,
        id: 'services'
    },
    {
        translation_key: 'sign_in_kiosk',
        text: 'Sign In Kiosk',
        icon: 'feather-smartphone',
        route: '/dashboard/sign_in_kiosk',
        is_new: true,
        is_hidden: false,
        id: 'sign_in_kiosk'
    },
    {
        translation_key: 'staff',
        text: 'Staff',
        icon: 'feather-user',
        route: '/dashboard/staff',
        is_hidden: false,
        id: 'staff'
    },
    // { separator: true },
    {
        translation_key: 'settings',
        text: 'Settings',
        icon: 'k-i-cog',
        route: '/dashboard/settings/basic',
        is_hidden: false,
        id: 'settings'
    },
    {
        translation_key: 'helps',
        text: 'Help',
        icon: 'k-i-help',
        external_link: 'http://help.aot.plus/',
        is_hidden: false,
        id: 'helps'
    },
    {
        translation_key: 'integrations',
        text: 'Integrations',
        icon: 'k-i-html',
        route: '/dashboard/integrations',
        is_hidden: false,
        id: 'integrations'
    },
]

export default function Sidebar({
    children,
    positionMode = "start",
    mode = "push",
    isMini = true,
    isExpanded
}: Props) {
    const router = useRouter()
    const ctx = useAuth()
    const { translations, loadComponentTranslations } = useAppContext()
    const { tenant, sidebar_items, setSidebarItems, is_free_tier } = useTenantContext()
    const isFullScreen = window.innerHeight === window.screen.height;
    let omitSidebarModules = ["notifications", "sign_in_kiosk", "settings", "helps"]
    
    // useEffect(() => {
    //     if (!translations?.SIDEBAR_TRANSLATION_ID) {
    //         loadComponentTranslations('SIDEBAR_TRANSLATION_ID')
    //     }
    // }, [translations])

    const handleClick = (e: MouseEvent) => {
        e.preventDefault()
        ctx.toggleDrawerSize(!ctx.is_drawer_expanded)
    }
    const onSelect = (e: DrawerSelectEvent) => {
        if (router.pathname == e.itemTarget.props.route) {
            return
        }
        if (e.itemTarget.props.external_link) {
            window.open(e.itemTarget.props.external_link, '_blank')?.focus()
            return
        }
        if (is_free_tier) {
            window.location.href = '/dashboard/profile/?upsell=true'
            return
        }
        if (e.itemTarget.props.open_modal) {
            ctx.POSModal.open()
            return
        }
        setSidebarItems(sidebar_items.map((item) => {
            return ({...item, selected: e.itemTarget.props.route == item.route}) 
        }))
        Cookies.set('drawer', 'expanded')
        // ctx.toggleDrawerSize(true)
        router.push(e.itemTarget.props.route)
    }

    const CustomItem = (props: DrawerItemProps & SidebarItem) => {
        const { tenants } = useAuth()

        if (props.expand_button) {
            return (
                <button
                    className={classNames(
                        'absolute bg-white text-dark-cyan-blue w-7 h-7 transition-all duration-200 right-0',
                        ctx.is_drawer_expanded
                            ? 'shadow-xl border-r border-indigo-50'
                            : '',
                            isFullScreen ? 'bottom-40' : 'bottom-20',
                        'duration-200 ease-linear transition-all rounded-full hover:bg-gray-300'
                    )}
                    id="BtnExpandSidebar"
                    onClick={handleClick}
                >
                    <span
                        className={classNames(
                            ctx.is_drawer_expanded
                                ? 'feather-chevron-left'
                                : 'feather-chevron-right',
                                'font-bold'
                        )}
                    ></span>
                </button>
            )
        }

        if (!props.icon && !props.route && !props.separator) {
            return (
                <div
                    className={classNames(
                        props.className || '',
                        'sidebar-tenant-logo'
                    )}
                    onClickCapture={props.onClickCapture}
                >
                    {' '}
                    <TenantSelector
                        tenant={tenant}
                        tenants={tenants}
                        translations={translations}
                    />
                </div>
            )
        } else if (props.separator) {
            return (
                <div className="justify-self-stretch flex-auto px-5">
                    <div
                        className="block border-b-gray-400 border-b h-full"
                        style={{ height: '1px' }}
                    />
                </div>
            )
        }
        
        return (
            <DrawerItem
                {...props}
                selected={router.pathname === props.route}
                className={classNames(
                    'flex items-center text-gray-600',
                    props.is_hidden
                        ? 'hidden'
                        : ''
                )}
            >
                <SidebarItem
                    item={props}
                />
            </DrawerItem>
        )
    }

    
    return (
        <Drawer
            className="w-full relative z-0 aot-sidebar"
            expanded={ctx.attributes?.hide_sidebar ? false : isExpanded ? isExpanded : ctx.is_drawer_expanded}
            mode={mode}
            mini={ctx.attributes?.hide_sidebar ? false : isMini}
            miniWidth={50}
            onSelect={onSelect}
            position={positionMode}
            style={{ height: 'calc(100vh - 60px)'  }}
            items={ctx.user?.uuid ? sidebar_items.filter((item) => !omitSidebarModules.includes(item.translation_key!)) : []}
            item={CustomItem}
        >
            <DrawerContent className={classNames(router.pathname.includes("/dashboard/calendar") ? "overflow-hidden" : "overflow-x-hidden", " h-full", "aot-content-area") }>
                {children}
            </DrawerContent>
        </Drawer>
    )
}

